import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

export default function AffiliatesPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://toolnova.ai';

  useSEO({
    title: 'Affiliate Disclosure — ToolNova',
    description: 'ToolNova affiliate disclosure. Some links on our site may be affiliate links. Our reviews and rankings are always based on independent editorial judgment.',
    keywords: 'affiliate disclosure, affiliate links, toolnova transparency',
    canonical: `${siteUrl}/affiliates`,
    ogTitle: 'Affiliate Disclosure — ToolNova',
    ogDescription: 'ToolNova is transparent about how we earn revenue. Read our full affiliate disclosure here.',
    ogType: 'article',
    ogUrl: `${siteUrl}/affiliates`,
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-10">
          <Link to="/" className="hover:text-teal-400 transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-gray-300">Affiliate Disclosure</span>
        </nav>

        {/* Page header */}
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/25 text-teal-400 text-xs font-semibold tracking-wide uppercase mb-6">
            <i className="ri-shield-check-line text-sm"></i>
            Transparency
          </div>
          <h1 className="text-4xl font-extrabold text-white mb-4 leading-tight">
            Affiliate Disclosure
          </h1>
          <p className="text-gray-400 text-lg leading-relaxed">
            ToolNova is committed to being open about how we operate and generate revenue.
          </p>
        </div>

        {/* Main disclosure */}
        <div className="bg-gray-900/60 border border-gray-800/80 rounded-2xl p-8 mb-8">
          <p className="text-gray-300 text-base leading-relaxed mb-6">
            Some of the links to AI tools on ToolNova may be affiliate links. This means ToolNova could earn a small commission if you sign up or purchase through those links, at no additional cost to you. Our reviews and rankings are always based on our independent editorial judgment, not on commissions.
          </p>
          <p className="text-gray-400 text-base leading-relaxed mb-6">
            We only recommend tools that we believe deliver genuine value to our readers. Whether or not a tool has an affiliate relationship with us has no bearing on how it is reviewed, ranked, or featured on the site.
          </p>
          <p className="text-gray-400 text-base leading-relaxed">
            This disclosure is made in accordance with the Federal Trade Commission&apos;s guidelines on endorsements and testimonials (16 CFR Part 255).
          </p>
        </div>

        {/* Three pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
          {[
            {
              icon: 'ri-money-dollar-circle-line',
              color: 'text-amber-400',
              bg: 'bg-amber-500/10 border-amber-500/25',
              title: 'How We Earn',
              body: 'We may earn a commission when you click a link and sign up or make a purchase. This comes from the tool provider — never from you.',
            },
            {
              icon: 'ri-shield-star-line',
              color: 'text-teal-400',
              bg: 'bg-teal-500/10 border-teal-500/25',
              title: 'Editorial Independence',
              body: 'Affiliate relationships never influence our ratings, reviews, or rankings. Every tool is evaluated on its own merit.',
            },
            {
              icon: 'ri-price-tag-3-line',
              color: 'text-sky-400',
              bg: 'bg-sky-500/10 border-sky-500/25',
              title: 'No Extra Cost to You',
              body: 'Clicking an affiliate link costs you nothing extra. You pay exactly the same price as visiting the tool\'s website directly.',
            },
          ].map((card) => (
            <div key={card.title} className={`rounded-xl border p-6 ${card.bg}`}>
              <div className={`w-10 h-10 flex items-center justify-center rounded-lg mb-4 ${card.bg}`}>
                <i className={`${card.icon} text-xl ${card.color}`}></i>
              </div>
              <h3 className="text-base font-bold text-white mb-2">{card.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{card.body}</p>
            </div>
          ))}
        </div>

        {/* Contact */}
        <div className="text-center py-10 border-t border-gray-800/60">
          <p className="text-gray-500 text-sm mb-4">
            Questions about our affiliate practices or editorial standards?
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-teal-500/40 text-gray-300 hover:text-teal-400 text-sm font-semibold rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-line"></i>
            Contact Us
          </Link>
        </div>

      </main>

      <Footer />
    </div>
  );
}
